<template>
  <line-chart
    title="Channels les plus performants"
    :loading="loading"
    :colors="colors"
    :series="series"
    :labels="labels"
    :yAxis="yAxis"
    :stroke="stroke"
    outlined
  ></line-chart>
</template>

<script>
import debounce from "lodash/debounce";

import LineChart from "@/components/common/charts/LineChart";

import useAxios from "@/hooks/useAxios";

import { colors } from "@/utils/constants";
import { formatNumber } from "@/utils/formatting";

const yAxis = (color = "") => ({
  min: 0,
  forceNiceScale: true,
  labels: {
    formatter: (val) => formatNumber(val),
    style: {
      fontSize: "12px",
      fontWeight: 400,
      ...(color ? { colors: color } : {}),
    },
  },
});

export default {
  name: "MostPerformingChannelsChart",
  components: {
    LineChart,
  },
  data() {
    return {
      loading: false,
      colors: [colors.sessions, colors.ca],
      series: [
        // Order must be the same than in yaxis definition here below
        {
          name: "sessions",
          data: [],
        },
        {
          name: "ca",
          data: [],
        },
      ],
      labels: [],
      yAxis: [
        // Order must be the same than in series definitions
        {
          seriesName: "sessions",
          title: {
            text: "Sessions",
            style: { color: colors.sessions },
          },
          ...yAxis(colors.sessions),
        },
        {
          opposite: true,
          seriesName: "ca",
          title: {
            text: "CA",
            style: { color: colors.ca },
          },
          ...yAxis(colors.ca),
        },
      ],
      stroke: {
        show: true,
        curve: "smooth",
        width: [2, 2],
      },
    };
  },
  setup() {
    const { axiosGet } = useAxios();

    return {
      axiosGet,
    };
  },
  methods: {
    debouncedGetData: debounce(async function () {
      await this.getData();
    }, 500),
    async getData() {
      if (
        this.applyFilters &&
        !this.loading &&
        this.startDate &&
        this.startDate.length === 10 &&
        this.endDate &&
        this.endDate.length === 10
      ) {
        this.loading = true;
        const { data } = await this.axiosGet(
          "/adn-post-channel/most-performing",
          {
            start_date: this.startDate,
            end_date: this.endDate,
            sites: this.sites.join(","),
            categories: this.categories.join(","),
            to_exclude: this.excludeCategories,
            channels: this.channels.join(","),
            period_type: this.periodType,
          }
        );

        this.labels = data.map((item) => item.channel);

        this.series = [
          // Order must be the same than in chartOptions.yaxis definition
          {
            name: "sessions",
            type: "line",
            data: data.map((item) => item.sessions),
          },
          {
            name: "ca",
            type: "column",
            data: data.map((item) => item.ca),
          },
        ];
        this.loading = false;

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        this.$store.dispatch("common/updateApplyFilters", false);
      }
    },
  },
  computed: {
    applyFilters() {
      return this.$store.getters["common/getApplyFilters"];
    },
    sites() {
      return this.$store.getters["audience/getSites"];
    },
    categories() {
      return this.$store.getters["audience/getCategories"];
    },
    excludeCategories() {
      return this.$store.getters["audience/getExcludeCategories"];
    },
    channels() {
      return this.$store.getters["audience/getChannels"];
    },
    startDate() {
      return this.$store.getters["audience/getDates"][0];
    },
    endDate() {
      return this.$store.getters["audience/getDates"][1];
    },
    periodType() {
      return this.$store.getters["audience/getPeriodType"];
    },
  },
  watch: {
    applyFilters() {
      this.debouncedGetData();
    },
  },
};
</script>
